import { useQuery } from '@apollo/client';
import { get } from 'lodash';
import { useRouter } from 'next/router';
import { Fragment, useMemo, useState } from 'react';
import Button from '~/components/Button';
import Divider from '~/components/Divider';
import Icon from '~/components/Icon';
import StoreReviewItem from '~/components/Items/StoreReviewItem';
import Select from '~/components/Select';
import Text from '~/components/Text';
import Colors from '~/constants/colors';
import { QUERY_GET_REVIEW_PRODUCT_DETAIL_PAGE } from '~/data/product/gql';
import { Reviews } from '~/data/product/interfaces';

const TabReviewsItem = () => {
    const router = useRouter();
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState('rating-DESC');

    const { data, fetchMore, networkStatus, loading } = useQuery(QUERY_GET_REVIEW_PRODUCT_DETAIL_PAGE, {
        variables: {
            param: router.query.slug as string,
            pageSize: 4,
            sort:
                sort && sort?.split('-').length > 0
                    ? {
                        [sort?.split('-')[0]]: sort?.split('-')[1]
                    }
                    : undefined
        },
        notifyOnNetworkStatusChange: true,
        context: { fetchOptions: { method: 'GET' } }
    });

    const reviews: Reviews = useMemo(() => {
        return get(data, 'products.items[0].reviews', {
            summary: 0,
            total_count: 0
        } as Reviews);
    }, [data]);

    const { items = [], page_info, total_count } = reviews || {};

    const handleShowMore = () => {
        if (networkStatus === 3) return;
        if (Number(Number(page_info?.page_size) * page) >= Number(total_count)) return;
        if (!!fetchMore) {
            const increasePage = page ? Number(page) + 1 : 1;
            setPage(increasePage);
            fetchMore({
                variables: {
                    currentPage: increasePage,
                    param: router.query.slug as string
                },
                updateQuery: (previousResult: any, { fetchMoreResult }: any) => {
                    if (!fetchMoreResult) return previousResult;
                    const newReviews = fetchMoreResult?.products?.items?.[0]?.reviews;
                    return {
                        products: {
                            ...previousResult?.products,
                            items: [
                                {
                                    ...previousResult?.products?.items?.[0],
                                    reviews: {
                                        ...newReviews,
                                        items: [
                                            ...previousResult?.products?.items?.[0]?.reviews?.items,
                                            ...newReviews?.items
                                        ]
                                    }
                                }
                            ]
                        }
                    };
                }
            });
        }
    };

    const isShowMore = networkStatus === 3 || Number(Number(page_info?.page_size) * page) < Number(total_count);

    const loadingReview = loading && [1, 2].includes(networkStatus);
    return (
        <div>
            <div className="w-[160px] ml-auto">
                <Select
                    className="flex-1"
                    si
                    options={[
                        { label: 'Highest Rating', value: 'rating-DESC' },
                        { label: 'Most recent', value: 'date-DESC' },
                        { label: 'Lowest Rating', value: 'rating-ASC' }
                    ]}
                    value={sort}
                    onChange={(value: string) => {
                        setSort(value);
                        setPage(1);
                    }}
                />
            </div>
            <div className="flex-1 flex flex-col gap-3 relative">
                {[...items, loadingReview ? [...Array.from({ length: 2 }).map(() => ({} as any))] : []]
                    .flat()
                    .map((item, index: number) => (
                        <Fragment key={index}>
                            <StoreReviewItem item={item} loading={loadingReview} />
                            {index !== items.length - 1 && <Divider />}
                        </Fragment>
                    ))}
                {isShowMore && (
                    <Button onClick={handleShowMore} type="borderless">
                        <Text type="title-4" className="flex items-center justify-center" color={Colors.PRIMARY}>
                            {networkStatus === 3 ? 'Showing' : 'Show More'}
                        </Text>
                        {networkStatus === 3 ? (
                            <Icon name="status" className="animate-spin mr-2 " size={'1rem'} />
                        ) : (
                            <Icon name="arrow-square-down-2" color={Colors.PRIMARY} />
                        )}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default TabReviewsItem;
